import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  AccessTime as AccessTimeIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Movie as MovieIcon,
  Home as HomeIcon,
  Translate as TranslateIcon,
  StopCircle as StopCircleIcon,
  MonitorHeart as MonitorHeartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { format, formatDistance } from 'date-fns';
import StatusService from '../../services/StatusService';
import  AdminPageWrapper from '../admin/AdminPageWrapper';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { videoProcessingHelpItems } from '../../utils/helpItems';
import {PageHeader} from '../common/PageHeader';
import IconButton from '@mui/material/IconButton';
import VideoCRUDService from '../../services/VideoCRUDService';
import Swal from 'sweetalert2';
import {isAdmin} from '../../utils/authUtils';
import statusServiceInstance from '../../services/StatusService';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';

const ProcessingStatusDashboard = ({darkMode, toggleDarkMode, themeVariant}) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('24h');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshKey, setRefreshKey] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    avgProcessingTime: 0,
    avgVideoProcessingTime: 0,
    avgTranslationTime: 0
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const navigate = useNavigate();
  

  useEffect(() => {
    fetchProcessingStatus();
    
    // Set up auto refresh every minute
    const refreshInterval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 60000);
    
    return () => clearInterval(refreshInterval);
  }, [timeframe, status, refreshKey]);
  
  const fetchProcessingStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      const response = await StatusService.getProcessingStatus(
        timeframe,
        status === 'all' ? null : status,
        authUser.email,
        authToken
      );
      
      if (response && response.data) {
        // Process videos to add calculated time fields
        const processedVideos = response.data.map(video => {
          // Add time information to languages
          const languagesWithTimes = (video.languages || []).map(lang => ({
            ...lang,
            translationTimeMinutes: calculateTimeDifference(
              lang.translationStartedAt,
              lang.translationCompletedAt
            )
          }));
          
          // Calculate the video processing time (separate from translation)
          const withLanguages = {
            ...video,
            languages: languagesWithTimes,
            isProcessingComplete: !!video.processingCompletedAt
          };
          
          // Calculate video processing time vs translation time
          const videoProcessingTimeMinutes = calculateVideoProcessingTime(withLanguages);
          const translationTimeMinutes = calculateTranslationTime(withLanguages.languages);
          
          // Total processing time includes both, add videoProcessingTimeMinutes and translationTimeMinutes
          // to get the total processing time
          const totalProcessingTimeMinutes = videoProcessingTimeMinutes + translationTimeMinutes;
          
          return {
            ...withLanguages,
            videoProcessingTimeMinutes,
            translationTimeMinutes,
            totalProcessingTimeMinutes
          };
        });
        
        setVideos(processedVideos);
        
        // Calculate statistics
        // Replace the simple completed calculation with this more comprehensive check
        const completed = processedVideos.filter(video => {
          // Only count as completed if the video processing is complete AND all languages have finished translation
          if (!video.processingCompletedAt) return false;
          
          // If no languages, just check the processingCompletedAt flag
          if (!video.languages || video.languages.length === 0) {
            return true;
          }
          
          // Check if all languages have completed translation
          return video.languages.every(lang => 
            lang.translationCompletedAt !== null || 
            lang.status === 'COMPLETED' || 
            lang.status === 'READY'
          );
        }).length;
        
        const inProgress = processedVideos.length - completed;
        
        // Calculate average processing time (only for completed videos)
        let totalCompletedTime = 0;
        let completedCount = 0;
        let totalVideoProcessingTime = 0;
        let totalTranslationTime = 0;
        
        processedVideos.forEach(video => {
          if(video.totalProcessingTimeMinutes) {
            totalCompletedTime += video.totalProcessingTimeMinutes;
          }
          if (video.processingCompletedAt) {
            completedCount++;
          }
          if (video.videoProcessingTimeMinutes) {
            totalVideoProcessingTime += video.videoProcessingTimeMinutes;
          }
          if (video.translationTimeMinutes) {
            totalTranslationTime += video.translationTimeMinutes;
          }

        });
        
        setStats({
          total: processedVideos.length,
          completed,
          inProgress,
          avgProcessingTime: completedCount > 0 ? (totalCompletedTime / completedCount) : 0,
          avgVideoProcessingTime: completedCount > 0 ? (totalVideoProcessingTime / completedCount) : 0,
          avgTranslationTime: completedCount > 0 ? (totalTranslationTime / completedCount) : 0
        });
      }
    } catch (err) {
      console.error("Error fetching processing status:", err);
      setError("Failed to fetch processing status. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm:ss a');
    } catch (e) {
      return dateStr;
    }
  };
  
  const getTimeSince = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistance(new Date(dateStr), new Date(), { addSuffix: true });
    } catch (e) {
      return '';
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PROCESSING': return 'primary';
      case 'PENDING': return 'warning';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };
  
  const calculateTimeDifference = (startDate, endDate) => {
    if (!startDate) return null;
    
    // If end date is not available, calculate time elapsed until now
    const end = endDate ? new Date(endDate) : new Date();
    const start = new Date(startDate);
    
    try {
      // Calculate difference in milliseconds
      const diffMs = end - start;
      
      // Convert to minutes with decimal precision for seconds
      return diffMs / (1000 * 60);
    } catch (e) {
      console.error("Error calculating time difference:", e);
      return null;
    }
  };

  const formatProcessingTime = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    
    // Convert minutes to a more precise value with seconds
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    if (hours > 0) {
      // Format: Xh Ym Zs
      return `${hours}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      // Format: Ym Zs
      return `${mins}m ${secs}s`;
    } else {
      // Format: Zs
      return `${secs}s`;
    }
  };

  // Calculate video processing time (time to download and compress the video)
  // This is separate from translation time
  const calculateVideoProcessingTime = (video) => {
    if (!video.processingStartedAt) return null;
    
   // If no translation has started yet, assume all time so far was video processing
    return calculateTimeDifference(video.processingStartedAt, video.processingCompletedAt);
  };

  // Calculate average translation time across all languages
  const calculateTranslationTime = (languages) => {
    if (!languages || languages.length === 0) return null;
    
    const completedTranslations = languages.filter(
      lang => lang.translationStartedAt && lang.translationCompletedAt
    );
    
    if (completedTranslations.length === 0) {
      // None completed yet, so calculate current elapsed time for active translations
      const activeTranslations = languages.filter(lang => lang.translationStartedAt);
      if (activeTranslations.length === 0) return null;
      
      const totalTimeMinutes = activeTranslations.reduce(
        (sum, lang) => sum + (lang.translationTimeMinutes || 0), 
        0
      );
      return totalTimeMinutes / activeTranslations.length;
    }
    
    // Calculate average time for completed translations
    const totalTimeMinutes = completedTranslations.reduce(
      (sum, lang) => sum + (lang.translationTimeMinutes || 0), 
        0
    );
    return totalTimeMinutes / completedTranslations.length;
  };

  // Get the maximum translation time (the longest translation)
  const getMaxTranslationTime = (languages) => {
    if (!languages || languages.length === 0) return null;
    
    const translationTimes = languages
      .map(lang => lang.translationTimeMinutes)
      .filter(Boolean);
    
    return translationTimes.length > 0 ? Math.max(...translationTimes) : null;
  };

  // Show processing timeline for the selected video
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  const handleTerminateTranslation = async (video, language) => {
    try {
        const status = await statusServiceInstance.getServerStatus();

        // if video.processingStartedAt is later than status.data.serverStartTime then do not 
        // since this means that threads will not be terminated 

        if (video.processingStartedAt && new Date(video.processingStartedAt) > new Date(status.data.serverStartTime)) {
            setSnackbar({
                open: true,
                message: `Translation for ${language.name} in video "${video.name}" started after server start time. Termination not allowed.`,
                severity: 'error'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Terminate Translation?',
            text: `Are you sure you want to terminate the translation for ${language.name} in video "${video.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, terminate it!'
        });

        if (result.isConfirmed) {
            await VideoCRUDService.terminateTranslation(video.id, language.name);

            language.status = 'TERMINATED'; // Update the language status locally
            
            setSnackbar({
                open: true,
                message: `Successfully terminated translation for ${language.name}`,
                severity: 'success'
            });
            
            // Refresh the dashboard
            fetchProcessingStatus();
        }
    } catch (error) {
        console.error('Error terminating translation:', error);
        setSnackbar({
            open: true,
            message: `Failed to terminate translation: ${error.message}`,
            severity: 'error'
        });
    }
};

  const renderLanguageStatus = (video, language) => {
      // Add explicit status check for both PROCESSING and processing
      const isProcessing = language.status !== 'READY' && language.status !== 'TERMINATED';
      
      return (
          <Box >
              {isProcessing && isAdmin && (
                <>
                  <Tooltip title="Terminate Translation">
                      <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleTerminateTranslation(video, language)}
                          sx={{ ml: 1 }}
                      >
                          <StopCircleIcon />
                      </IconButton>
                  </Tooltip>
                  
                  <Typography variant="body2" fontWeight="medium">
                        {language.name} Translation {language.status}
                  </Typography>
                  </>
              )}
              {!isProcessing && (
                  <Typography variant="body2" color={getStatusColor(language.status)}>
                      {language.name} Translation {language.status}
                  </Typography>
              )}
          </Box>
      );
  };

  return (
    <Box sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
                // Remove margin to allow full width
            }}>
      <Paper sx={{
        borderRadius: 0,
        padding: 0
      }}>
        <PageHeader 
          darkMode={darkMode} 
          toggleDarkMode={toggleDarkMode}
          showLogout={true}
          themeVariant={themeVariant} 
        />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 , padding: 1}}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            Home
          </Button>
          {/* Add arrow icon between buttons */}
          <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
          <Button
            variant="text"
            color="secondary"
            startIcon={<MonitorHeartIcon />}
            sx={{ mr: 2 }}
          >
            System
          </Button>
          <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
          <Button
            variant="text"
            color="secondary"
            startIcon={<VideoFileIcon />}
            sx={{ mr: 2 }}
          >
            Video Processing Status Dashboard
          </Button>
        </Box>
        {/* Update this Box to fill the width */}
        <Box sx={{ 
            mb: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            flexWrap: 'wrap', 
            gap: 2,
            width: '100%' 
        }}>
          {/* Update this inner Box to fill the width */}
          <Box sx={{ mb: 3, width: '100%' }}>            
            {/* Make sure the Paper components also fill the width */}
            <Paper sx={{ p: 2, mb: 3, width: '100%' }} className="processing-status-summary">
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }} className="processing-filters">
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={timeframe}
                      onChange={(e) => setTimeframe(e.target.value)}
                    >
                      <MenuItem value="1h">Last hour</MenuItem>
                      <MenuItem value="6h">Last 6 hours</MenuItem>
                      <MenuItem value="12h">Last 12 hours</MenuItem>
                      <MenuItem value="24h">Last 24 hours</MenuItem>
                      <MenuItem value="7d">Last 7 days</MenuItem>
                      <MenuItem value="30d">Last 30 days</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <MenuItem value="all">All videos</MenuItem>
                      <MenuItem value="in_progress">In progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    variant="outlined"
                    size="small"
                  >
                    Refresh
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary">
                  Auto-refreshes every minute
                </Typography>
              </Box>
              
              {/* Stats cards - single row layout */}
              <Grid container spacing={2}>
                {/* Count cards */}
                <Grid item xs={12} sm={6} md={2}>
                  <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">Total Videos</Typography>
                      <Typography variant="h4">{stats.total}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">In Progress</Typography>
                      <Typography variant="h4" color="primary">{stats.inProgress}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">Completed</Typography>
                      <Typography variant="h4" color="success.main">{stats.completed}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Separate processing time cards */}
                <Grid item xs={12} sm={6} md={2}>
                  <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">Video Processing (Avg)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">Download & Compression</Typography>
                      <Typography variant="h5">
                        {stats.avgVideoProcessingTime > 0 
                          ? formatProcessingTime(stats.avgVideoProcessingTime) 
                          : 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">Translation Time (Avg)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">S2TT & Translation</Typography>
                      <Typography variant="h5">
                        {stats.avgTranslationTime > 0 
                          ? formatProcessingTime(stats.avgTranslationTime) 
                          : 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Card elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">Total Time (Avg)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">End-to-End Duration</Typography>
                      <Typography variant="h5">
                        {stats.avgProcessingTime > 0 
                          ? formatProcessingTime(stats.avgProcessingTime) 
                          : 'N/A'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : videos.length === 0 ? (
              <Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  bgcolor: darkMode ? 'background.paper' : undefined
                }} 
                className="video-processing-queue"
              >
                <MovieIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No video processing data found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try changing the timeframe or status filter
                </Typography>
              </Paper>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ width: '100%' }}>
                  <Table sx={{
                    width: '100%',
                    tableLayout: 'fixed', // This helps with column width distribution
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)'
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                      backgroundColor: darkMode ? 'background.paper' : 'background.default'
                    }
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell width="20%">Video Name</TableCell>
                        <TableCell width="10%">Created By</TableCell>
                        <TableCell width="10%">Started</TableCell>
                        <TableCell width="12%">Video Processing</TableCell>
                        <TableCell width="18%">Languages</TableCell>
                        <TableCell width="12%">Translation Time</TableCell>
                        <TableCell width="10%">Total Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {videos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((video) => {
                        const isComplete = !!video.processingCompletedAt;
                        const translationTime = calculateTranslationTime(video.languages);
                        
                        return (
                          <TableRow 
                            key={video.id}
                            hover
                            onClick={() => setSelectedVideo(video)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                {video.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {video.createdBy ? video.createdBy.replace(/@vaani\.org/g, '') : ''}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={formatDateTime(video.processingStartedAt)}>
                                <Typography variant="body2">
                                  {getTimeSince(video.processingStartedAt)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            {/* Video Processing Time */}
                            <TableCell>
                              <Tooltip title="Time to download and compress the video">
                                <Typography variant="body2">
                                  {formatProcessingTime(video.videoProcessingTimeMinutes)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            {/* Languages */}
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(video.languages || []).map((lang, idx) => {
                                  // Determine which icon to show based on status and completion
                                  let statusIcon;
                                  let chipColor = getStatusColor(lang.status);
                                  
                                  if (lang.status === 'PENDING' || (lang.status === 'PROCESSING' && lang.translationCompletedAt === null)) {
                                    // Pending or still processing
                                    statusIcon = <PendingIcon fontSize="small" />;
                                  } else if (lang.status === 'PROCESSING') {
                                    // In progress but has started
                                    statusIcon = <AccessTimeIcon fontSize="small" />;
                                  } else if (lang.status === 'COMPLETED' || lang.status === 'READY') {
                                    // Completed
                                    statusIcon = <CheckCircleIcon fontSize="small" />;
                                  } else if (lang.status === 'FAILED') {
                                    // Failed
                                    statusIcon = <ErrorIcon fontSize="small" />;
                                  }
                                  
                                  const tooltipTitle = lang.translationCompletedAt 
                                    ? `${lang.name}: ${lang.status} (${formatProcessingTime(lang.translationTimeMinutes)})`
                                    : `${lang.name}: ${lang.status} (Pending)`;
                                  
                                  return (
                                    <Tooltip 
                                      key={idx}
                                      title={tooltipTitle}
                                    >
                                      <Chip
                                        label={lang.name}
                                        size="small"
                                        color={chipColor}
                                        sx={{ 
                                          fontSize: '0.7rem',
                                          '& .MuiChip-icon': { 
                                            marginLeft: '5px' 
                                          }
                                        }}
                                        icon={statusIcon}
                                      />
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            </TableCell>
                            {/* Translation Time */}
                            <TableCell>
                              <Tooltip title="Average time to process all languages">
                                <Typography variant="body2">
                                  {formatProcessingTime(translationTime)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            {/* Total Processing Time */}
                            <TableCell>
                              <Tooltip title="Total time from start to completion">
                                <Typography variant="body2">
                                  {formatProcessingTime(video.totalProcessingTimeMinutes)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={videos.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Box>
          
          {/* Video processing timeline */}
          {selectedVideo && (
            <Paper sx={{ p: 3, mb: 3, width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Processing Timeline: {selectedVideo.name}
                </Typography>
                <Button size="small" onClick={() => setSelectedVideo(null)}>Close</Button>
              </Box>
              
              <Timeline position="alternate">
                {/* Processing Started */}
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color="primary">
                      <MovieIcon fontSize="small" />
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="body2" fontWeight="medium">
                      Processing Started
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(selectedVideo.processingStartedAt)}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                
                {/* Video Processing Phase */}
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color="secondary">
                      <AccessTimeIcon fontSize="small" />
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="body2" fontWeight="medium">
                      Video Processing Phase
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Duration: {formatProcessingTime(selectedVideo.videoProcessingTimeMinutes)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      (Download and compression)
                    </Typography>
                  </TimelineContent>
                </TimelineItem>

                {/* Translation Phase Header */}
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot color="info">
                      <TranslateIcon fontSize="small" />
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="body2" fontWeight="medium">
                      Translation Phase
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Average Duration: {formatProcessingTime(selectedVideo.translationTimeMinutes)}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>

                {/* Individual Languages */}
                {selectedVideo.languages && selectedVideo.languages.map((lang, idx) => (
                  <TimelineItem key={idx}>
                    <TimelineSeparator>
                      <TimelineDot color={lang.status === 'READY' ? 'success' : 'warning'}>
                        {lang.status === 'READY' ? 
                          <CheckCircleIcon fontSize="small" /> : 
                          <AccessTimeIcon fontSize="small" />}
                      </TimelineDot>
                      {idx < selectedVideo.languages.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent>
                      {renderLanguageStatus(selectedVideo, lang)}
                      
                      <Typography variant="caption" color="text.secondary">
                        Started: {formatDateTime(lang.translationStartedAt)}
                      </Typography>
                      
                      {lang.translationCompletedAt ? (
                        <>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Completed: {formatDateTime(lang.translationCompletedAt)}
                          </Typography>
                          <Typography variant="caption" color="primary" display="block">
                            Duration: {formatProcessingTime(lang.translationTimeMinutes)}
                          </Typography>
                        </>
                      ) : lang.translationStartedAt && (
                        <>
                          <Typography variant="caption" color="text.secondary" display="block">
                            In progress: {getTimeSince(lang.translationStartedAt)}
                          </Typography>
                          <Typography variant="caption" color="primary" display="block">
                            Elapsed: {formatProcessingTime(lang.translationTimeMinutes)}
                          </Typography>
                        </>
                      )}
                    </TimelineContent>
                  </TimelineItem>
                ))}

                {/* Processing Completed (show only if completed) */}
                {selectedVideo.processingCompletedAt ? (
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="success">
                        <CheckCircleIcon fontSize="small" />
                      </TimelineDot>
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" fontWeight="medium">
                        Processing Completed
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(selectedVideo.processingCompletedAt)}
                      </Typography>
                      <Box sx={{ 
                        mt: 1, 
                        p: 1, 
                        bgcolor: darkMode ? 'background.paper' : 'background.default', 
                        borderRadius: 1 
                      }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <strong>Video Processing:</strong> {formatProcessingTime(selectedVideo.videoProcessingTimeMinutes)}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (Download and compression)
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <strong>Translation (Avg):</strong> {formatProcessingTime(selectedVideo.translationTimeMinutes)}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (Speech-to-text and translations)
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="primary" display="block">
                          <strong>Total Processing Time:</strong> {formatProcessingTime(selectedVideo.totalProcessingTimeMinutes)}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (End-to-end duration)
                          </Typography>
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                ) : (
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="primary">
                        <AccessTimeIcon fontSize="small" />
                      </TimelineDot>
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" fontWeight="medium">
                        Processing In Progress
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Elapsed: {formatProcessingTime(selectedVideo.totalProcessingTimeMinutes)}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
              </Timeline>
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ProcessingStatusDashboard;